import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop, {
  type Crop,
  type PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { cn } from '@/lib/utils';
import { Button } from './button';
import {
  cropImage,
  calculateCropRegion,
  getImageDimensions,
  getBestCompressionFormat,
} from '@/lib/image-processing';
import type { CropPosition, CropRegion, ImageProcessingOptions } from '@/types/image';

// ============================================================================
// Types
// ============================================================================

export interface ImageCropperProps {
  /** Source image URL to crop */
  src: string;
  /** Whether the cropper modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when crop is applied with the resulting blob and region */
  onCrop: (croppedBlob: Blob, region: CropRegion, previewUrl: string) => void;
  /** Target aspect ratio (1 = square, 4/3 = landscape, 3/4 = portrait) */
  targetAspectRatio?: number;
  /** Initial crop position preset */
  initialCropPosition?: CropPosition;
  /** Compression options for the output */
  compressionOptions?: Partial<ImageProcessingOptions>;
  /** Label for the modal */
  label?: string;
  /** Whether to show the compression settings panel */
  showCompressionSettings?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ImageCropper Component
 *
 * A modal-based image cropping interface that provides:
 * - react-image-crop integration for precise selection
 * - Preset crop positions (center, top, custom)
 * - Aspect ratio locking
 * - Live preview of cropped result
 * - Compression quality control
 * - Keyboard shortcuts (Esc to close, Enter to apply)
 *
 * @example
 * ```tsx
 * <ImageCropper
 *   src={imageUrl}
 *   isOpen={isCropperOpen}
 *   onClose={() => setIsCropperOpen(false)}
 *   onCrop={(blob, region) => handleCrop(blob, region)}
 *   targetAspectRatio={1}
 *   initialCropPosition="center"
 * />
 * ```
 */
export function ImageCropper({
  src,
  isOpen,
  onClose,
  onCrop,
  targetAspectRatio = 1,
  initialCropPosition = 'center',
  compressionOptions,
  label = 'Crop Image',
  showCompressionSettings = false,
}: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [cropPosition, setCropPosition] = useState<CropPosition>(initialCropPosition);
  const [lockAspectRatio, setLockAspectRatio] = useState(true);
  const [quality, setQuality] = useState(
    compressionOptions?.compressQuality ?? 0.85
  );
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [error, setError] = useState<string>('');

  const imgRef = useRef<HTMLImageElement>(null);

  // Load image dimensions on open
  useEffect(() => {
    if (isOpen && src) {
      setError('');
      setPreviewUrl('');
      setCompletedCrop(undefined);
      getImageDimensions(src)
        .then((dims) => {
          setOriginalDimensions({ width: dims.width, height: dims.height });

          // Initialize crop based on position preset
          const cropRegion = calculateCropRegion(
            dims.width,
            dims.height,
            Math.round(dims.height * targetAspectRatio),
            dims.height,
            cropPosition
          );

          // Convert percentage crop to react-image-crop format
          const initialCrop: Crop = {
            unit: '%',
            x: cropRegion.x,
            y: cropRegion.y,
            width: cropRegion.width,
            height: cropRegion.height,
          };

          setCrop(initialCrop);
        })
        .catch((err) => {
          console.error('[ImageCropper] Failed to get image dimensions:', err);
          setError('Failed to load image dimensions');
        });
    }
  }, [isOpen, src, targetAspectRatio, cropPosition]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'Enter' && !isProcessing) {
        e.preventDefault();
        handleApplyCrop();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isProcessing, onClose]);

  // Generate preview when crop changes
  useEffect(() => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const previewDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setPreviewUrl(previewDataUrl);
  }, [completedCrop]);

  // Handle image load to set initial crop
  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      if (targetAspectRatio > 0 && lockAspectRatio) {
        const { width, height } = e.currentTarget;
        const center = centerCrop(
          makeAspectCrop(
            { unit: '%', width: 90, height: 90 },
            targetAspectRatio,
            width,
            height
          ),
          width,
          height
        );
        setCrop(center);
      }
    },
    [targetAspectRatio, lockAspectRatio]
  );

  // Apply the crop
  const handleApplyCrop = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      setError('Please select a crop area first');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Calculate output dimensions
      const outputWidth = Math.round(completedCrop.width * scaleX);
      const outputHeight = Math.round(completedCrop.height * scaleY);

      // Calculate crop region as percentages
      const region: CropRegion = {
        x: completedCrop.x / image.width,
        y: completedCrop.y / image.height,
        width: completedCrop.width / image.width,
        height: completedCrop.height / image.height,
      };

      // Crop the image
      const croppedDataUrl = await cropImage(
        src,
        region,
        outputWidth,
        outputHeight
      );

      // Convert data URL to Blob
      const response = await fetch(croppedDataUrl);
      const blob = await response.blob();

      // Compress if needed
      let finalBlob = blob;
      if (quality < 1) {
        const compressCanvas = document.createElement('canvas');
        compressCanvas.width = outputWidth;
        compressCanvas.height = outputHeight;
        const ctx = compressCanvas.getContext('2d');
        if (ctx) {
          const img = new Image();
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = reject;
            img.src = croppedDataUrl;
          });
          ctx.drawImage(img, 0, 0, outputWidth, outputHeight);
          const format = getBestCompressionFormat();
          const mimeType = format === 'webp' ? 'image/webp' : 'image/jpeg';
          finalBlob = await new Promise<Blob>((resolve, reject) => {
            compressCanvas.toBlob(
              (b) => (b ? resolve(b) : reject(new Error('Compression failed'))),
              mimeType,
              quality
            );
          });
        }
      }

      onCrop(finalBlob, region, croppedDataUrl);
      onClose();
    } catch (err) {
      console.error('[ImageCropper] Crop failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to crop image');
    } finally {
      setIsProcessing(false);
    }
  }, [completedCrop, src, quality, onCrop, onClose]);

  // Handle preset position change
  const handlePresetChange = useCallback(
    (position: CropPosition) => {
      setCropPosition(position);

      if (!originalDimensions) return;

      const cropRegion = calculateCropRegion(
        originalDimensions.width,
        originalDimensions.height,
        Math.round(originalDimensions.height * targetAspectRatio),
        originalDimensions.height,
        position
      );

      setCrop({
        unit: '%',
        x: cropRegion.x,
        y: cropRegion.y,
        width: cropRegion.width,
        height: cropRegion.height,
      });
    },
    [originalDimensions, targetAspectRatio]
  );

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm p-3 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div className="pointer-events-none flex min-h-full items-start justify-center sm:items-center">
        <div
          className="pointer-events-auto flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          data-testid="cropper-panel"
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-brand-100 px-4 py-4 sm:px-6">
            <div>
              <h2 className="text-lg font-semibold text-brand-900">{label}</h2>
              <p className="mt-1 text-sm text-brand-500">
                Centered, square crops work best for most product photos.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-brand-50 text-brand-500 hover:text-brand-700 transition-colors"
              aria-label="Close cropper"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto" data-testid="cropper-content">
            <div className="space-y-6 p-4 sm:p-6">
              {/* Crop Area */}
              <div className="flex flex-col gap-6 lg:flex-row">
                {/* Main Crop Area */}
                <div className="flex-1 min-w-0">
                  <div className="relative overflow-hidden rounded-xl bg-brand-50">
                    <ReactCrop
                      crop={crop}
                      onChange={(_, percentCrop) => setCrop(percentCrop)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={lockAspectRatio ? targetAspectRatio : undefined}
                      minHeight={50}
                      minWidth={50}
                      className="max-h-[50vh] sm:max-h-[420px]"
                    >
                      <img
                        ref={imgRef}
                        src={src}
                        alt="Image to crop"
                        onLoad={onImageLoad}
                        className="mx-auto block max-h-[50vh] w-auto sm:max-h-[420px]"
                        crossOrigin="anonymous"
                      />
                    </ReactCrop>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}
                </div>

                {/* Preview Panel */}
                <div className="flex flex-col items-center lg:w-48">
                  <span className="mb-2 text-sm font-medium text-brand-700">Preview</span>
                  <div className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-xl border border-brand-200 bg-brand-50">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Crop preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="p-4 text-center text-xs text-brand-400">
                        Adjust the crop to see preview
                      </div>
                    )}
                  </div>
                  {completedCrop && (
                    <div className="mt-2 text-center text-xs text-brand-500">
                      {completedCrop.width.toFixed(0)} x {completedCrop.height.toFixed(0)}
                    </div>
                  )}
                </div>
              </div>

              {/* Controls */}
              <div className="space-y-4">
                {/* Crop Position Presets */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-brand-700">
                    Suggested framing
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(['center', 'top', 'custom'] as CropPosition[]).map((position) => (
                      <button
                        key={position}
                        type="button"
                        onClick={() => handlePresetChange(position)}
                        className={cn(
                          'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                          cropPosition === position
                            ? 'bg-brand-600 text-white'
                            : 'border border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100'
                        )}
                      >
                        {position === 'center'
                          ? 'Center'
                          : position === 'top'
                            ? 'Top'
                            : 'Custom'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Aspect Ratio Lock */}
                <div className="flex flex-wrap items-center gap-3">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={lockAspectRatio}
                      onChange={(e) => setLockAspectRatio(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="h-6 w-11 rounded-full bg-brand-200 peer peer-checked:bg-brand-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full after:absolute after:start-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:after:border-white"></div>
                  </label>
                  <span className="text-sm text-brand-700">Lock aspect ratio</span>
                  {lockAspectRatio && (
                    <span className="text-xs text-brand-500">
                      ({targetAspectRatio === 1
                        ? 'Square (1:1)'
                        : targetAspectRatio === 4 / 3
                          ? 'Landscape (4:3)'
                          : targetAspectRatio === 3 / 4
                            ? 'Portrait (3:4)'
                            : `${targetAspectRatio}:1`})
                    </span>
                  )}
                </div>

                {/* Compression Settings */}
                {showCompressionSettings && (
                  <div className="rounded-xl bg-brand-50 p-4">
                    <label className="mb-2 block text-sm font-medium text-brand-700">
                      Output Quality
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="50"
                        max="100"
                        value={quality * 100}
                        onChange={(e) => setQuality(Number(e.target.value) / 100)}
                        className="h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-brand-200 accent-brand-600"
                      />
                      <span className="w-12 text-right font-mono text-sm text-brand-700">
                        {Math.round(quality * 100)}%
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-brand-500">
                      Lower quality = smaller file size. Recommended: 80-90%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-brand-100 bg-brand-50/50 px-4 py-4 sm:px-6">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyCrop}
              disabled={isProcessing || !completedCrop}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Processing...
                </>
              ) : (
                'Apply Crop'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;
